import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './button';
import { ErrorState } from './states';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The last line of defence.
 *
 * Without this, a render error anywhere unmounts the whole tree and leaves a
 * blank white page — indistinguishable, to the person looking at it, from a
 * bundle that failed to load or an nginx misconfiguration. Saying "this page
 * broke" and leaving the shell standing is a much shorter support conversation.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only sink these apps have; there is no error
    // reporting service, and adding one is a decision about sending customer
    // data to a third party, not a frontend detail.
    console.error('unhandled render error', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <ErrorState
          title="This page failed to render"
          description={this.state.error.message}
          action={
            <Button variant="outline" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}
